import React from "react";
import { Switch, Redirect, Route } from "react-router-dom";
import { IRoute } from "./RouteInterface";
import { RouteWithSubRoutes } from "./RouteWithSubRoutes";
import { UpdateIntercom } from "../components/utility/intercom";
import { DashboardContainer } from "../components/layouts/DashboardContainer";
import { AuthContext } from "../auth/AuthProvider";
import { PageRoutes } from "../constants/constants";

/**
 * Landing for a URL no route matches. Sending a signed-in user to the sign-in page reads as an
 * unexplained sign-out, so they go to company selection instead and keep their session.
 */
const UnmatchedRoute: React.FC = () => {
  const { currentUser, loader } = React.useContext(AuthContext);

  if (loader) {
    return null;
  }

  return <Redirect to={currentUser ? PageRoutes.SWITCH_COMPANY : "/signin"} />;
};

interface IProps {
  routes: IRoute[];
}

export const Router: React.FC<IProps> = ({ routes }) => {
  UpdateIntercom();

  const routesWithSidebar = React.useMemo(() => routes.filter((route: IRoute) => route.showSidebar), [routes]);
  const routesWithoutSidebar = React.useMemo(() => routes.filter((route: IRoute) => !route.showSidebar), [routes]);

  const sidebarRoutePaths = React.useMemo(
    () => routesWithSidebar.flatMap((route) => (Array.isArray(route.path) ? route.path : [route.path])),
    [routesWithSidebar]
  );

  return (
    <Switch>
      {sidebarRoutePaths.length > 0 && (
        <Route path={sidebarRoutePaths} exact>
          <DashboardContainer hideBackground showSidebar>
            <Switch>
              {routesWithSidebar.map((route: IRoute) => (
                <RouteWithSubRoutes key={route.path.toString()} {...route} />
              ))}
            </Switch>
          </DashboardContainer>
        </Route>
      )}
      {routesWithoutSidebar.map((route: IRoute) => (
        <RouteWithSubRoutes key={route.path.toString()} {...route} />
      ))}
      <Route path="*">
        <UnmatchedRoute />
      </Route>
    </Switch>
  );
};
